import React, { useEffect, useState, useMemo, useRef } from 'react';
import { LuEye, LuFileText } from 'react-icons/lu';
import ReactQuill, { Quill } from 'react-quill-new'; 
import ImageResize from 'quill-image-resize-module-react';
import 'react-quill-new/dist/quill.snow.css';
import '../../styles/AdminNoticeTab.css'; // 기존 스타일 유지 또는 업데이트

window.Quill = Quill;
Quill.register('modules/imageResize', ImageResize);

const API_BASE = process.env.REACT_APP_API_BASE || '';

export default function AdminNoticeTab() {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState('');
  const [activeLang, setActiveLang] = useState('kr'); 
  const [uploadedImages, setUploadedImages] = useState([]);

  const getTemplateForm = () => ({
    id: null, 
    date: new Date().toISOString().split('T')[0],
    category: 'NOTICE',
    views: 0,
    is_visible: 1,
    title_kr: '', title_en: '', title_jp: '', title_ch: '',
    content_kr: '', content_en: '', content_jp: '', content_ch: ''
  });

  const quillRefs = {
    kr: useRef(null), en: useRef(null), jp: useRef(null), ch: useRef(null)
  };

  const [n, setN] = useState(getTemplateForm);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // 이미지 핸들러 (서버 업로드 및 에디터 삽입)
  const imageHandler = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await fetch(`${API_BASE}/api/admin_image_upload.php`, {
          method: 'POST', body: formData, credentials: 'include'
        });
        const data = await res.json();

        const currentQuill = quillRefs[activeLang].current.getEditor();
        const range = currentQuill.getSelection(true);
        const imageUrl = data.url.startsWith('http') ? data.url : `${API_BASE}${data.url}`;
        
        currentQuill.insertEmbed(range.index, 'image', imageUrl);
        currentQuill.setSelection(range.index + 1);

        setUploadedImages(prev => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      } catch (e) { alert('이미지 업로드 실패'); }
    };
  };

  // 취소 시 이미지 정리
  const handleCancel = async () => {
    if (uploadedImages.length > 0) {
      if (!window.confirm('작성 중인 내용과 업로드된 이미지가 모두 삭제됩니다. 취소하시겠습니까?')) return;
      try {
        await fetch(`${API_BASE}/api/admin_image_delete.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ urls: uploadedImages }),
          credentials: 'include'
        });
      } catch (e) { console.error("이미지 정리 실패:", e); }
    }
    setShowModal(false);
    setUploadedImages([]);
    setN(getTemplateForm());
  };

  // 이미지 모든 언어 복사 (현재 언어 제외)
  const copyToAllLangs = (url) => {
    const langs = ['kr', 'en', 'jp', 'ch'];
    langs.forEach(lang => {
      if (lang === activeLang) return;
      const editor = quillRefs[lang].current.getEditor();
      const range = editor.getSelection() || { index: editor.getLength() };
      editor.insertEmbed(range.index, 'image', url);
    });
    setMsg('이미지가 다른 언어에도 추가되었습니다. ✅');
    setTimeout(() => setMsg(''), 2000);
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        ['image', 'link'], ['clean']
      ],
      handlers: { image: imageHandler }
    },
    imageResize: { modules: ['Resize', 'DisplaySize', 'Toolbar'] }
  }), [activeLang]);

  async function load() {
    try {
      const res = await fetch(`${API_BASE}/api/admin_notice_list.php`, { credentials: 'include' });
      const data = await res.json();
      setItems(Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : []);
    } catch (e) { setMsg('서버 연결 오류'); }
  }

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = isEditMode ? `${API_BASE}/api/admin_notice_update.php` : `${API_BASE}/api/admin_notice_create.php`;

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(n),
      });

      if(res.ok) {
        setMsg(isEditMode ? 'Updated ✅' : 'Saved ✅');
        setShowModal(false);
        setUploadedImages([]);
        load();
        setTimeout(() => setMsg(''), 2000);
      }
    } catch (error) { alert('저장 실패'); }
  };

  const isFilled = (lang) => {
    const title = n[`title_${lang}`]?.trim() || '';
    const content = n[`content_${lang}`]?.replace(/<[^>]*>?/gm, '').trim() || '';
    return title.length > 0 && content.length > 0;
  };

  const renderLangSection = (lang) => (
    <div key={lang} style={{ display: activeLang === lang ? 'block' : 'none' }}>
      <div className="quill-outer-wrapper">
        <label className="editor-label label">내용 ({lang.toUpperCase()})</label>
        <ReactQuill 
          ref={quillRefs[lang]}
          theme="snow" 
          modules={modules} 
          value={n[`content_${lang}`] || ''} 
          onChange={val => setN(prev => ({...prev, [`content_${lang}`]: val}))} 
        />
      </div>
    </div>
  );

  return (
    <div className="admin-tab-content news-admin">
      {msg && <div className={`admin-toast ${msg.includes('✅') ? 'success' : 'error'}`}>{msg}</div>}

      <div className="admin-list-header">
        <button onClick={() => { setN(getTemplateForm()); setIsEditMode(false); setShowModal(true); setActiveLang('kr'); setUploadedImages([]); }} className="admin-btn primary">+ NEW NOTICE</button>
      </div>

      <section className='admin-card'>
        <div className="card-head"><h3>NOTICE LIST</h3><span className="card-dot"></span></div>
        <div className="admin-notice-list">
          {items.map(it => (
            <div key={it.id} className="notice-admin-item">
              <div className="notice-info">
                <div className="notice-meta">
                  <span className="notice-date">{it.date}</span>
                  <span className="notice-cat-badge">{it.category}</span>
                  <span className="notice-cat-badge">{Number(it.is_visible) === 1 ? '노출' : '숨김'}</span>
                  <span className="item-views">
                    <LuEye size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    {Number(it.views || 0).toLocaleString()}
                  </span>
                </div>
                <h4 className="notice-title">{it.title_kr}</h4>
              </div>
              <div className="notice-actions">
                <button onClick={() => { setN(it); setIsEditMode(true); setActiveLang('kr'); setShowModal(true); setUploadedImages([]); }} className="icon-btn edit">✎</button>
                <button onClick={async () => { if(window.confirm('삭제하시겠습니까?')) { await fetch(`${API_BASE}/api/admin_notice_delete.php`, {method:'POST', body: JSON.stringify({id: it.id}), credentials:'include'}); load(); }}} className="icon-btn del">✕</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-card modal-card news-modal">
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="modal-header">
                <h3>{isEditMode ? 'EDIT NOTICE' : 'CREATE NOTICE'}</h3>
                <div className="lang-selector-wrapper">
                  <select value={activeLang} onChange={(e) => setActiveLang(e.target.value)} className="lang-select-box">
                    <option value="kr">KR {isFilled('kr') ? '✅' : ''}</option>
                    <option value="en">EN {isFilled('en') ? '✅' : ''}</option>
                    <option value="jp">JP {isFilled('jp') ? '✅' : ''}</option>
                    <option value="ch">CH {isFilled('ch') ? '✅' : ''}</option>
                  </select>
                </div>
              </div>

              {uploadedImages.length > 0 && (
                <div className="uploaded-images-bar">
                  <span className="bar-label">이미지:</span>
                  <div className="img-chips">
                    {uploadedImages.map((url, i) => (
                      <div key={i} className="img-chip" onClick={() => copyToAllLangs(url)}>
                        <img src={url} alt="uploaded" />
                        <span className="chip-hover">추가</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="input-row inline-row">
                <div className="input-group date-group">
                  <label>날짜</label>
                  <input type="date" value={n.date} onChange={e => setN({...n, date: e.target.value})} required />
                </div>
                <div className="input-group title-group">
                  <label>제목</label>
                  <input 
                    type="text" 
                    value={n[`title_${activeLang}`]} 
                    onChange={e => setN({...n, [`title_${activeLang}`]: e.target.value})} 
                    placeholder="제목을 입력하세요"
                    required={activeLang === 'kr'}
                  />
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-end', gap: '8px', whiteSpace: 'nowrap', marginLeft: 'auto', minHeight: '42px' }}>
                  <input
                    type="checkbox"
                    checked={Number(n.is_visible) === 1}
                    onChange={e => setN({ ...n, is_visible: e.target.checked ? 1 : 0 })}
                  />
                  VIEW
                </label>
              </div>

              <div className="lang-specific-section">
                {['kr', 'en', 'jp', 'ch'].map(lang => renderLangSection(lang))}
              </div>

              <div className="form-actions">
                <button type="submit" className="admin-btn primary">SAVE</button>
                <button type="button" onClick={handleCancel} className="admin-btn secondary">CANCEL</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
